import { Badge, Card } from "antd";
import { PresetStatusColorType } from "antd/lib/_util/colors";
import { Equipment } from "api/generated";
import { ReactNode, useMemo } from "react";

export type EquipmentStatus = NonNullable<Equipment["status"]>;
type StatusConfig = {
  color: PresetStatusColorType;
  text: string;
  online: boolean;
};

const STATUS_CONFIG: Partial<Record<EquipmentStatus, StatusConfig>> = {
  DESOCUPADO: { color: "success", text: "Desocupado", online: true },
  SURTIENDO: { color: "processing", text: "Surtiendo", online: true },
  FUERA_LINEA: { color: "error", text: "Fuera de línea", online: false },
  SIN_COMUNICACION: { color: "error", text: "Sin comunicación", online: false },
  ERR_COMUNICACION: {
    color: "error",
    text: "Error comunicación",
    online: false,
  },
  DESHABILITADO: { color: "default", text: "Deshabilitado", online: false },
  PENDIENTE_ENLACE: {
    color: "default",
    text: "Pendiente de Enlace",
    online: false,
  },
  ELIMINADO: { color: "default", text: "Eliminado", online: false },
  SINCRONIZANDO: { color: "warning", text: "Sincronizando", online: true },
  AUTORIZANDO: { color: "warning", text: "Autorizando", online: true },
  INICIALIZANDO: { color: "warning", text: "Inicializando", online: true },
  LOGIN: { color: "processing", text: "Login", online: true },
  MENU: { color: "processing", text: "Menú", online: true },
  NOTIFICANDO: { color: "processing", text: "Notificando", online: true },
};

export function getStatusConfig(status: EquipmentStatus) {
  return (
    STATUS_CONFIG[status] ?? {
      color: "default",
      text: status ?? "Desconocido",
      online: false,
    }
  );
}

interface EquipmentCardProps {
  icon: ReactNode;
  status: EquipmentStatus;
  isActive: boolean;
  content: ReactNode;
  footer: ReactNode;
}

function EquipmentCard({
  icon,
  status,
  isActive,
  content,
  footer,
}: EquipmentCardProps) {
  const statusConfig = useMemo(() => getStatusConfig(status), [status]);

  return (
    <Badge.Ribbon
      text={statusConfig.text}
      color={
        statusConfig.color === "success"
          ? "green"
          : statusConfig.color === "error"
            ? "red"
            : statusConfig.color === "warning"
              ? "orange"
              : statusConfig.color === "processing"
                ? "blue"
                : "gray"
      }
    >
      <Card
        style={{ opacity: isActive ? 1 : 0.75 }}
        styles={{ body: { padding: 0 } }}
      >
        {/* Header azul con icono */}
        <div
          style={{
            background: "linear-gradient(135deg, #1890ff 0%, #096dd9 100%)",
            height: 100,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Círculos decorativos */}
          <div
            style={{
              position: "absolute",
              top: -30,
              right: -30,
              width: 100,
              height: 100,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.1)",
            }}
          />
          <div
            style={{
              position: "absolute",
              bottom: -20,
              left: -20,
              width: 80,
              height: 80,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.05)",
            }}
          />

          {/* Icono principal */}
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: 16,
              background: "rgba(255,255,255,0.2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1,
            }}
          >
            {icon}
          </div>
        </div>

        {/* Contenido */}
        <div style={{ padding: 16 }}>
          {content}

          <div
            style={{
              paddingTop: 12,
              borderTop: "1px solid #f0f0f0",
              marginBottom: 12,
            }}
          >
            {/* Footer */}
            {footer}
          </div>
        </div>
      </Card>
    </Badge.Ribbon>
  );
}
export default EquipmentCard;
